import { Module, ModuleProgressStats } from "./types";

// Mappage des IDs de cours vers les IDs YouTube
// Centralise toutes les associations spéciales dans un seul objet
export const COURSE_TO_YOUTUBE_ID_MAP: Record<string, string> = {
  "1": "dQw4w9WgXcQ", // ID YouTube pour le cours 1
  "2": "7HBt_xBhhPM", // ID YouTube pour le cours 2
  // Ajouter de nouveaux mappages ici au fur et à mesure que de nouveaux cours sont ajoutés
};

// Fonction pour extraire l'ID de la vidéo YouTube à partir de l'URL
export const extractYoutubeId = (url: string | undefined): string => {
  if (!url) return "";

  // Vérifier d'abord si l'URL est un ID de cours dans notre mappage
  const courseId = String(url).trim();
  if (COURSE_TO_YOUTUBE_ID_MAP[courseId]) {
    return COURSE_TO_YOUTUBE_ID_MAP[courseId];
  }

  // Essayer de trouver l'ID YouTube dans différents formats d'URL
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);

  // Si on trouve un ID YouTube valide, le retourner
  if (match && match[2].length === 11) {
    return match[2];
  }

  // Dernière vérification: l'URL est-elle déjà un ID YouTube valide (11 caractères)?
  if (url.length === 11 && /^[a-zA-Z0-9_-]{11}$/.test(url)) {
    return url;
  }

  return "";
};

// Fonction pour obtenir l'URL de la miniature YouTube à partir de l'ID
export const getYoutubeThumbnail = (url: string | undefined): string => {
  const videoId = extractYoutubeId(url);
  if (!videoId) return `/images/slide2.jpg`; // Image par défaut si aucun ID valide

  // Utilisez la miniature haute qualité de YouTube
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
};

// Fonction pour formater la durée en minutes
export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min`;
  } else {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}min`
      : `${hours}h`;
  }
};

// Fonction pour calculer la progression d'un module
export const calculateModuleProgress = (
  module: Module,
  courseProgresses: { courseId: string; completed: boolean }[]
): ModuleProgressStats => {
  // Vérifier si les données de progression sont valides
  if (!Array.isArray(courseProgresses) || courseProgresses.length === 0) {
    return { completed: 0, total: 0, percent: 0 };
  }

  // Vérifier si le module existe
  if (!module) {
    return { completed: 0, total: 0, percent: 0 };
  }

  // Cas où le module n'a pas de cours listés explicitement mais a des progressions
  if (!module.courses || module.courses.length === 0) {
    // Utiliser les progressions comme source de vérité pour le nombre de cours
    const coursesFromProgress = courseProgresses.filter((p) => {
      // On suppose que les courses avec le moduleId dans le courseId sont liés à ce module
      if (p.courseId && module.id) {
        return String(p.courseId).includes(String(module.id));
      }
      return false;
    });

    const total = coursesFromProgress.length;
    const completed = coursesFromProgress.filter((p) => p.completed).length;
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

    console.log(
      `Module ${module.id} sans courses mais avec progressions: ${completed}/${total} (${percent}%)`
    );
    return { completed, total, percent };
  }

  // Cas standard: utiliser les cours du module et les progressions
  const total = module.courses.length;
  const completed = Array.isArray(courseProgresses)
    ? module.courses.filter((course) =>
        courseProgresses.some((p) => p.courseId === course.id && p.completed)
      ).length
    : 0;

  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  return { completed, total, percent };
};
